import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = 'nexflow_clock_last_v1';
const CACHE_MAX_MS = 16 * 3600 * 1000;
const subs = new Set();

// Last server-CONFIRMED clock state, shared by every gate/banner/page instance and
// persisted so a reload / PWA resume starts from the known state instead of a
// "clocked out" or blank flash while the server re-validates.
export const readClockCache = (uid) => {
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    return c && uid && c.uid === uid && Date.now() - (c.at || 0) < CACHE_MAX_MS ? c : null;
  } catch { return null; }
};
export const publishClockState = (uid, data) => {
  if (!uid || !data) return;
  const required = !!data.time_tracking_required;
  const c = { uid, required, clockedIn: required ? !!data.clocked_in : true, at: Date.now() };
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch { /* ignore */ }
  subs.forEach((fn) => fn(c));
};

// iter253 — the Field-Schedule clock gate keys off each employee's TIME-TRACKING
// configuration (backend `time_tracking_required`), NOT their role. Polls every 30s,
// on `nexflow:clock-changed`, and when the app returns to the foreground.
// iter316 — clockedIn true | false (server CONFIRMED out) | null (unknown). A failed or
// in-flight check is NEVER treated as clocked out; the last confirmed state is kept.
export function useClockGate() {
  const { user } = useAuth();
  const uid = user?._id || user?.id || '';
  const cached = readClockCache(uid);
  const [required, setRequired] = useState(cached ? cached.required : false);
  const [clockedIn, setClockedIn] = useState(cached ? cached.clockedIn : null);
  const [loading, setLoading] = useState(!cached);
  const [checking, setChecking] = useState(false);
  const mounted = useRef(true);
  const lastRef = useRef(cached ? cached.clockedIn : null);

  const refresh = useCallback(async () => {
    if (mounted.current) setChecking(true);
    try {
      const { data } = await axios.get(`${API}/timeclock/status`, { withCredentials: true });
      const req = !!data?.time_tracking_required;
      const ci = req ? !!data?.clocked_in : true;
      lastRef.current = ci;
      publishClockState(uid, data);
      if (mounted.current) { setRequired(req); setClockedIn(ci); setLoading(false); }
      return ci;
    } catch {
      if (mounted.current && lastRef.current !== null) setLoading(false);
      return lastRef.current;
    } finally {
      if (mounted.current) setChecking(false);
    }
  }, [uid]);

  useEffect(() => {
    mounted.current = true;
    const onShared = (c) => {
      if (c.uid !== uid || !mounted.current) return;
      lastRef.current = c.clockedIn;
      setRequired(c.required); setClockedIn(c.clockedIn); setLoading(false);
    };
    subs.add(onShared);
    refresh();
    const iv = setInterval(refresh, 30000);
    const onChange = () => refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('nexflow:clock-changed', onChange);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onChange);
    const retry = setInterval(() => { if (lastRef.current === null) refresh(); }, 5000);
    return () => {
      mounted.current = false;
      subs.delete(onShared);
      clearInterval(iv);
      clearInterval(retry);
      window.removeEventListener('nexflow:clock-changed', onChange);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onChange);
    };
  }, [refresh, uid]);

  return { required, clockedIn, loading, checking, refresh };
}
